import axiosHandler from "@/utils/axios-handler";
import { academyApiUrl } from '@/lib/constants/api';

export interface UserCourseActivity {
  entryUid: string;
  progress?: {
    percentage: number;
    hasStarted: boolean;
    isCompleted: boolean;
  };
  isSubscribed?: boolean;
  isBookmarked?: boolean;
  isLiked?: boolean;
  lastViewedAt?: string;
  updatedAt?: string;
  lessons?: UserLessonActivity[];
}

export interface UserLessonActivity {
  entryUid: string;
  courseEntryUid: string;
  isBookmarked?: boolean;
  isLiked?: boolean;
  progress?: {
    percentage: number;
    hasStarted: boolean;
    isCompleted: boolean;
    assessment?: {
      attemptLog?: unknown[];
      totalAttempts?: number;
      lastAttemptedAt?: string | null;
      maxPercentageAchieved?: number;
    };
  };
  lastViewedAt?: string;
  updatedAt?: string;
}

/** Response from GET /v1/users/courses/:courseUid/lessons/:lessonUid - used for quiz revisit/refresh */
export type LessonWithProgressResponse = UserLessonActivity;

/**
 * Fetch user activity for a specific course
 */
export const getUserCourseActivity = async (
  courseEntryUid: string
): Promise<UserCourseActivity | null> => {
  try {
    const options = {
      method: "GET",
      url: academyApiUrl(`/v1/users/courses/${courseEntryUid}`),
      withCredentials: true,
    };

    const response = await axiosHandler(options);

    if (response && "status" in response && response.status === 200 && "data" in response) {
      return response.data as UserCourseActivity;
    }

    return null;
  } catch (error) {
    // Error fetching user activity for course
    return null;
  }
};

/**
 * Fetch user activity for a specific lesson
 */
export const getUserLessonActivity = async (
  courseEntryUid: string,
  lessonEntryUid: string
): Promise<UserLessonActivity | null> => {
  try {
    const options = {
      method: "GET",
      url: academyApiUrl(`/v1/users/courses/${courseEntryUid}/lessons/${lessonEntryUid}`),
      withCredentials: true,
    };

    const response = await axiosHandler(options);

    if (response && "status" in response && response.status === 200 && "data" in response) {
      return response.data as UserLessonActivity;
    }

    return null;
  } catch (error) {
    // Error fetching user activity for lesson
    return null;
  }
};

/**
 * Fetch user activity for either a course or lesson
 */
export const getUserActivity = async ({
  courseEntryUid,
  lessonEntryUid,
}: {
  courseEntryUid: string;
  lessonEntryUid?: string;
}): Promise<{
  courseData: UserCourseActivity | null;
  lessonData?: UserLessonActivity | null;
}> => {
  let courseData: UserCourseActivity | null = null;
  let lessonData: UserLessonActivity | null = null;

  
  if (lessonEntryUid) {
    lessonData = await getUserLessonActivity(courseEntryUid, lessonEntryUid);
  } else {
    courseData = await getUserCourseActivity(courseEntryUid);
  }

  return { courseData, lessonData };
};
